'use client';
import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Icon } from '#pplx/frontend/aether/components/Icon/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { domainFromURL } from '#pplx/frontend/common/files/urls.js';
import * as Delimiter from '#pplx/frontend/components/DelimiterList.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { $icon } from '#pplx/frontend/icons/icon-sprite.js';
import { Link } from '#pplx/frontend/router/Link.js';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mergeRefs } from 'react-merge-refs';
const LightboxImage = Dynamic(async () => {
    const { LightboxImage } = await import('#pplx/frontend/components/overlays/LightboxImage.js');
    return { default: LightboxImage };
});
function getImageShadowProps(hasShadow, loaded, includeLightBoxModal, showLightBox) {
    if (hasShadow && loaded) {
        return { shadowSize: 'sm', hoverShadow: 'sm' };
    }
    if (hasShadow || (includeLightBoxModal && !showLightBox)) {
        return { hoverShadow: 'sm' };
    }
    return {};
}
// Simple image with a fallback.
const Image = React.memo(function Image({ src, lightboxSrc, alt, fadeIn, fallbackSrc, fallback, hasShadow = false, includeLightBoxModal = true, lightboxFooter, origin, draggable = true, imageClassName, maskClassName, containerClassName, rounded = 'md', onClick, AIProps, authorName, authorUrl, onLoad, onFail, softBlockSaveImage = false, imageRef, imageProps, testId, onDownload, referrerPolicy, ...props }) {
    const [failed, setFailed] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [showLightBox, setShowLightBox] = useState(false);
    const imgRef = useRef(null);
    const { isAI, AILabel } = AIProps || { isAI: false, AILabel: '' };
    const mountTimeRef = useRef(Date.now());
    const onError = useCallback((event) => {
        setFailed(true);
        onFail?.({
            isConnected: event.currentTarget.isConnected,
            loadTime: Date.now() - mountTimeRef.current,
        });
    }, [onFail]);
    const onLoadInternal = useCallback(() => {
        setLoaded(true);
        onLoad?.();
    }, [setLoaded, onLoad]);
    // img.onload is not guaranteed to be called (e.g. if the image is cached
    // by the browser).  Guarantee it by setting the img.src after the onload
    // and onerror have been attached after render.
    useEffect(() => {
        if (!imgRef.current) {
            return;
        }
        if (imgRef.current.complete) {
            onLoadInternal();
            return;
        }
        if (imgRef.current.src) {
            return;
        }
        // @ts-expect-error TS(2322) FIXME: Type 'string | undefined' is not assignable to typ... Remove this comment to see the full error message
        imgRef.current.src = src;
    }, [src, onLoadInternal, imgRef]);
    const className = classNames(imageClassName, 'transition-all ease-in-out', {
        'opacity-100 duration-200 scale-100': loaded && fadeIn,
        'opacity-0 scale-[0.98]': !loaded && fadeIn,
        'hidden opacity-0': failed && !fallbackSrc,
        'max-h-[90vh]': includeLightBoxModal,
        'cursor-zoom-in duration-200': includeLightBoxModal && !showLightBox,
    });
    const sharedImgProps = useMemo(() => ({
        className,
        src,
        alt,
        onError,
        onClick: () => setShowLightBox(true),
        ...imageProps,
    }), [alt, className, imageProps, onError, src]);
    const baseImgProps = useMemo(() => ({
        ref: imgRef,
        onLoad: onLoadInternal,
        ...sharedImgProps,
    }), [onLoadInternal, sharedImgProps]);
    const lightboxImgProps = useMemo(() => ({
        ...sharedImgProps,
        src: lightboxSrc ?? src,
    }), [lightboxSrc, sharedImgProps, src]);
    const handleAuthorClick = useCallback((e) => {
        // Stop propagation so the click doesn't trigger the parent item handler
        e.stopPropagation();
    }, []);
    const authorLinkContent = (_jsxs(_Fragment, { children: [_jsx(TextPrimitive, { variant: "tiny", color: "white", className: "px-1 py-two h-5 rounded-md bg-black/60 opacity-0 transition-all group-hover:opacity-100", children: _jsxs(Delimiter.Root, { className: "gap-1", children: [_jsx(Delimiter.Item, { id: "authorName", children: authorName }), authorUrl ? (_jsx(Delimiter.Item, { id: "authorDomain", children: domainFromURL(authorUrl) })) : null] }) }), _jsx("div", { className: "size-1 bg-black/60 opacity-0 transition-all group-hover:opacity-100" })] }));
    const onClose = useCallback(() => setShowLightBox(false), []);
    if (failed) {
        if (fallbackSrc && imgRef.current) {
            imgRef.current.src = fallbackSrc;
        }
        else if (fallback !== undefined) {
            return fallback;
        }
    }
    return (_jsxs(_Fragment, { children: [_jsx("div", { onClick: onClick, className: containerClassName, "data-testid": testId, children: _jsxs(BoxPrimitive, { noBorder: true, shadowLayer: "both", ...getImageShadowProps(hasShadow, loaded, includeLightBoxModal, showLightBox), className: classNames('bg-subtle group relative size-full overflow-hidden', maskClassName, {
                        'transition-all duration-200 ease-in-out hover:scale-[1.02]': hasShadow,
                        'rounded-xs': rounded === 'sm',
                        'rounded-md': rounded === 'md',
                        'rounded-sm': rounded === true,
                    }), children: [isAI && (_jsxs("div", { className: "bottom-1 right-1 absolute z-10 flex items-center justify-center", children: [_jsx("div", { children: _jsx(TextPrimitive, { variant: "tiny", color: "white", className: "gap-x-two px-1 py-two flex h-5 items-center rounded-md bg-black/60 opacity-0 transition-all group-hover:opacity-100", children: _jsx("span", { children: AILabel }) }) }), _jsx("div", { className: "size-1 bg-black/60 opacity-0 transition-all group-hover:opacity-100" }), _jsx("div", { children: _jsx(TextPrimitive, { variant: "tiny", color: "white", className: "gap-x-two p-two flex h-5 items-center rounded-md bg-black/60", children: _jsx(Icon, { icon: $icon('cpu-2'), size: "tiny" }) }) })] })), authorName &&
                            (authorUrl ? (_jsx(Link, { href: authorUrl ?? '', target: "_blank", rel: "noopener noreferrer", onClick: handleAuthorClick, className: "bottom-1 right-1 absolute z-10 flex items-center justify-center", children: authorLinkContent })) : (_jsx("div", { onClick: handleAuthorClick, className: "bottom-1 right-1 absolute z-10 flex items-center justify-center", children: authorLinkContent }))), softBlockSaveImage && !isAI && (_jsx("div", { className: "absolute inset-0", onClick: includeLightBoxModal ? () => setShowLightBox(true) : undefined })), _jsx("img", { width: props.width, height: props.height, 
                            // @ts-expect-error TS(2783) FIXME: 'alt' is specified more than once, so this usage w... Remove this comment to see the full error message
                            alt: alt, ...baseImgProps, ref: mergeRefs([imageRef, imgRef]), draggable: draggable, referrerPolicy: referrerPolicy })] }) }), includeLightBoxModal && (_jsx(LightboxImage, { onClose: onClose, isOpen: showLightBox, imgProps: lightboxImgProps, origin: origin, width: props.width, height: props.height, alt: alt, onDownload: onDownload, footer: lightboxFooter }))] }));
});
export default Image;
//# sourceMappingURL=Image.js.map